import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { RiArrowLeftLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { type IconLike } from './icon';
import { IconChip } from './icon-chip';
import { type ChipColor } from './lib/colors';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { Stepper, type StepperProps } from './stepper';

export interface SidePanelProps {
  /** 300 (default, 501:10798) or 400 (531:12161 employee page). */
  width?: 300 | 400;
  /** "Ortga qaytish" back row; hidden without `onBack`. */
  onBack?: () => void;
  backLabel?: React.ReactNode;
  /** Title row: chip 32 r8 (15 glyph) + `t-sheet-title`. */
  icon?: { icon: IconLike; color?: ChipColor };
  title?: React.ReactNode;
  /** Right side of the title row (status badge "Faol"…). */
  titleAside?: React.ReactNode;
  /** Replaces the default title row (e.g. avatar + name + subtitle, 532:12201). */
  header?: React.ReactNode;
  /** Wizard stepper under the title (516:11317). */
  stepper?: Pick<StepperProps, 'steps' | 'current' | 'onStepClick'>;
  /** Slot under the header (e.g. `<Tabs>` 532:12209). */
  tabs?: React.ReactNode;
  children?: React.ReactNode;
  /** Sticky footer: full-width button or Secondary + Primary pair. */
  footer?: React.ReactNode;
  /** Body padding/gap off (custom layouts such as tables or lists). */
  bodyPadding?: boolean;
  /** 1px White/10 line on top (the panel sits under the page header in 501:10798). Default true. */
  topBorder?: boolean;
  as?: 'aside' | 'section' | 'div';
  'aria-label'?: string;
  className?: string;
  bodyClassName?: string;
}

const Divider = () => <div aria-hidden className="h-px shrink-0 bg-line" />;

/** "Ortga qaytish" link row (h42, padding 13/16, gap 8, arrow-left-line 16 + 600 13/auto `#35b5ec`). */
export function SidePanelBack({ onClick, children = 'Ortga qaytish' }: { onClick: () => void; children?: React.ReactNode }) {
  return (
    <div className="flex h-42 shrink-0 items-center px-16">
      <button
        type="button"
        onClick={onClick}
        className="-mx-4 flex cursor-pointer items-center gap-8 rounded-4 px-4 py-2 text-13 leading-auto font-semibold text-link transition-opacity hover:opacity-80"
      >
        <RiArrowLeftLine aria-hidden className="size-16" />
        {children}
      </button>
    </div>
  );
}

/**
 * Right side panel / side sheet chrome (spec §6.8, 501:10798): `bg-panel` + left White/10 line, back row, title row,
 * optional stepper / tabs, scrollable body (padding 16/16/18/16, gap 14) and a sticky footer (padding 14/16/16/16).
 * Docked layout component — place it in the page's right column. For an overlay use `SideSheet`.
 */
export function SidePanel({
  width = 300,
  onBack,
  backLabel,
  icon,
  title,
  titleAside,
  header,
  stepper,
  tabs,
  children,
  footer,
  bodyPadding = true,
  topBorder = true,
  as: Tag = 'aside',
  'aria-label': ariaLabel,
  className,
  bodyClassName,
}: SidePanelProps) {
  return (
    <Tag
      aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
      className={cx(
        'flex h-full min-h-0 shrink-0 flex-col border-l border-line bg-panel text-fg',
        width === 300 ? 'w-300' : 'w-400',
        className,
      )}
    >
      {topBorder && <Divider />}
      {onBack && (
        <>
          <SidePanelBack onClick={onBack}>{backLabel}</SidePanelBack>
          <Divider />
        </>
      )}
      {header ??
        (title != null && (
          <>
            <div className="flex min-h-60 shrink-0 items-center gap-10 px-16 py-14">
              {icon && <IconChip icon={icon.icon} color={icon.color ?? 'green'} size={32} className="[&>svg]:size-15" />}
              <h2 className="min-w-0 flex-1 truncate t-sheet-title">{title}</h2>
              {titleAside}
            </div>
            <Divider />
          </>
        ))}
      {header && <Divider />}
      {stepper && (
        <>
          <Stepper {...stepper} />
          <Divider />
        </>
      )}
      {tabs}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-y-auto',
          bodyPadding && 'gap-14 px-16 pt-16 pb-18',
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer && <div className="flex shrink-0 items-center gap-10 px-16 pt-14 pb-16 [&>*]:flex-1">{footer}</div>}
    </Tag>
  );
}

export interface SideSheetProps extends Omit<SidePanelProps, 'as'> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Dim the page and trap focus (default false: the page stays interactive, like the docked panel). */
  modal?: boolean;
  container?: PortalContainer;
}

/** The side panel as an overlay sheet sliding in from the right (Base UI Dialog). */
export function SideSheet({ open, onOpenChange, modal = false, container, className, ...panel }: SideSheetProps) {
  const portalContainer = usePortalContainer(container);
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined} modal={modal}>
      <BaseDialog.Portal container={portalContainer}>
        {modal && (
          <BaseDialog.Backdrop className="fixed inset-0 z-60 bg-overlay transition-opacity duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0" />
        )}
        <BaseDialog.Popup
          className={cn(
            'fixed top-0 right-0 bottom-0 z-70 shadow-float outline-none transition-transform duration-200 ease-standard',
            'data-starting-style:translate-x-full data-ending-style:translate-x-full',
          )}
        >
          <BaseDialog.Title className="sr-only">{typeof panel.title === 'string' ? panel.title : panel['aria-label']}</BaseDialog.Title>
          <SidePanel {...panel} as="div" className={className} />
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
